/**
 * Geometri Güveni - drawing where the model came from, not what it looks like.
 *
 * The model screen answers "is my scan any good?" with a picture, and a picture
 * of a completed surface is indistinguishable from a picture of a measured one.
 * That is the whole problem: the more convincing a completion is, the less a
 * photograph of it tells you. So there is a second way to look at the same
 * geometry in which the surface is coloured by *where it came from* rather than
 * by what the cameras saw on it.
 *
 * ## Colour is never the only channel
 *
 * Each class carries a label, a sentence and a share of the model, and the
 * legend prints all three. Somebody who cannot separate the two blues still
 * reads "Yapay zekâ ile tamamlanan - %12", which is the fact. The colours are
 * additionally chosen to differ in lightness as well as hue, so they survive a
 * greyscale print and the common forms of colour blindness.
 *
 * ## An unlabelled model is unverified, never observed
 *
 * A model built before the mesh stage measured provenance carries no attribute
 * at all. It is drawn entirely as *kaydı olmayan*, which says the pipeline has
 * no record - and specifically does not say the surface was invented, which
 * would be its own false claim about somebody's existing work.
 */

import { SURFACE_PROVENANCE_BY_CODE, type Mesh } from "./glb";
import { SURFACE_PROVENANCE_TERMS } from "@/i18n/terms";
import type { SurfaceProvenance } from "@/api/types";

/**
 * Linear RGB per class, in the order of `SURFACE_PROVENANCE_BY_CODE`.
 *
 * Observed is a calm neutral green, weakly observed an amber, completed a
 * strong magenta and unrecorded a grey. Magenta rather than red: red reads as
 * "error", and a completed surface in a project that asked for completion is
 * working exactly as the customer requested. Their lightness values are
 * spread - roughly 0.45, 0.62, 0.50 and 0.55 - so the four remain distinct
 * without hue.
 */
export const PROVENANCE_COLOURS: Record<SurfaceProvenance, [number, number, number]> = {
  observed: [0.29, 0.62, 0.45],
  weakly_observed: [0.88, 0.7, 0.28],
  ai_completed: [0.79, 0.35, 0.72],
  unverified: [0.58, 0.58, 0.6],
};

/** CSS for the legend swatch, from the same numbers the shader gets. */
export const provenanceSwatch = (value: SurfaceProvenance): string => {
  const [r, g, b] = PROVENANCE_COLOURS[value];
  return `rgb(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)})`;
};

export interface ProvenanceShare {
  value: SurfaceProvenance;
  label: string;
  note: string;
  vertices: number;
  /** 0..1 of the model's vertices. */
  share: number;
}

/**
 * What the model is made of, as the legend states it.
 *
 * Every class is returned, including the ones with no vertices, and the legend
 * hides the empty ones rather than this function omitting them - a caller that
 * wants to say "no completed surface at all" needs the zero to exist.
 */
export function provenanceShares(mesh: Mesh): ProvenanceShare[] {
  const total = mesh.vertexCount || 1;
  const tally: number[] = new Array<number>(SURFACE_PROVENANCE_BY_CODE.length).fill(0);
  if (mesh.provenance) {
    for (let i = 0; i < mesh.provenance.length; i += 1) {
      // A code this build does not know is unrecorded, not observed. A newer
      // pipeline adding a class must never silently downgrade to the most
      // trusting answer in an older viewer.
      const code: number = mesh.provenance[i] ?? 3;
      const slot = code < tally.length ? code : 3;
      tally[slot] = (tally[slot] ?? 0) + 1;
    }
  } else {
    tally[3] = mesh.vertexCount;
  }
  return SURFACE_PROVENANCE_BY_CODE.map((value, code) => ({
    value,
    label: SURFACE_PROVENANCE_TERMS[value].label,
    note: SURFACE_PROVENANCE_TERMS[value].note,
    vertices: tally[code]!,
    share: tally[code]! / total,
  }));
}

/**
 * The provenance of the surface a click landed on.
 *
 * The weakest of the hit triangle's three corners, for the same reason a face
 * takes the weakest of its vertices on the server: a point on a triangle with
 * one completed corner sits on surface whose position depends on invented
 * data, wherever inside the triangle it happens to be.
 *
 * A model with no provenance answers `unverified`, which is what makes a
 * measurement on an older model carry an honest note rather than a silent
 * claim to have been checked.
 */
export function provenanceAt(
  mesh: Mesh,
  triangle: number,
): SurfaceProvenance {
  if (!mesh.provenance) return "unverified";
  let worst = 0;
  for (let corner = 0; corner < 3; corner += 1) {
    const vertex = mesh.indices[triangle + corner];
    if (vertex === undefined) return "unverified";
    const code = mesh.provenance[vertex] ?? 3;
    // Higher code is less trustworthy - the order in
    // `SURFACE_PROVENANCE_BY_CODE` is the trust order - so the weakest corner
    // is the largest code.
    if (code > worst) worst = code;
  }
  return SURFACE_PROVENANCE_BY_CODE[worst] ?? "unverified";
}

/**
 * Whether a measurement between these surfaces may be presented plainly.
 *
 * Both ends have to be a real observation. `weakly_observed` counts: it is a
 * photograph of the world, and refusing to measure it would make the tool
 * useless on exactly the regions people most want to check. What does not
 * count is geometry a model produced and geometry with no recorded evidence at
 * all - and those two are kept apart in the sentence the panel shows, because
 * "we invented this" and "we do not know" are different things to tell
 * somebody. Mirrors `domain.geometry_mode.measurement_is_verified`.
 */
export const measurementIsVerified = (
  ...values: readonly SurfaceProvenance[]
): boolean =>
  values.length > 0 && values.every((value) => SURFACE_PROVENANCE_TERMS[value].measurable);

/** True when any part of this model was created rather than measured. */
export const hasCompletedSurface = (mesh: Mesh): boolean =>
  mesh.provenance !== null && mesh.provenance.some((code) => code === 2);

/** True when the file says nothing about where any of its surface came from. */
export const provenanceIsUnrecorded = (mesh: Mesh): boolean => mesh.provenance === null;

/**
 * A colour per vertex for the trust view, in the layout the surface shader's
 * existing `color` attribute already expects.
 *
 * Deliberately the same attribute rather than a new one. The trust view is a
 * different *colour* for the same geometry under the same light, so it is one
 * buffer swap - and the viewer keeps one lighting model instead of two shader
 * programs that have to be kept in agreement.
 */
export function provenanceColours(mesh: Mesh): Float32Array {
  const colours = new Float32Array(mesh.vertexCount * 3);
  const unrecorded = PROVENANCE_COLOURS.unverified;
  for (let v = 0; v < mesh.vertexCount; v += 1) {
    const code = mesh.provenance ? (mesh.provenance[v] ?? 3) : 3;
    const name = SURFACE_PROVENANCE_BY_CODE[code];
    const [r, g, b] = name ? PROVENANCE_COLOURS[name] : unrecorded;
    colours[v * 3] = r;
    colours[v * 3 + 1] = g;
    colours[v * 3 + 2] = b;
  }
  return colours;
}
